import { useEffect, useRef } from 'react';
import { hideSidebar, showSidebar } from '../utils/sidebarUtils';
import { saveContent } from '../utils/contentManager';
import { createEnhancedToolbar } from '../components/ToolbarFactory';
import { transformResourceStringsToSpans } from '../utils/contentTransforms';

export const useContentEditable = (
    isEditing: boolean,
    tempContent: string,
    setTempContent: (content: string) => void,
    contentRef: React.RefObject<HTMLDivElement>,
    allFonts: Array<{ name: string } | string>,
    colorVariables: string[],
    widgetInstanceId: string,
    setIsEditing: (editing: boolean) => void,
    templateResourceStrings?: Record<string, string>,
    siteResourceStrings?: Record<string, string>,
    authorizationToken?: string,
    data?: any
) => {
    const cleanupRef = useRef<() => void>();

    // Use refs to capture latest values without causing re-renders
    const allFontsRef = useRef(allFonts);
    const colorVariablesRef = useRef(colorVariables);
    const setTempContentRef = useRef(setTempContent);
    const setIsEditingRef = useRef(setIsEditing);
    const templateResourceStringsRef = useRef(templateResourceStrings);
    const siteResourceStringsRef = useRef(siteResourceStrings);

    // Update refs when values change
    allFontsRef.current = allFonts;
    colorVariablesRef.current = colorVariables;
    setTempContentRef.current = setTempContent;
    setIsEditingRef.current = setIsEditing;
    templateResourceStringsRef.current = templateResourceStrings;
    siteResourceStringsRef.current = siteResourceStrings;

    // Setup contentEditable when in edit mode
    useEffect(() => {
        const setupContentEditable = () => {
            if (contentRef.current) {
                console.log('Setting up contentEditable');

                // Set contentEditable BEFORE setting innerHTML
                contentRef.current.contentEditable = 'true';
                contentRef.current.setAttribute('spellcheck', 'false');

                // Only set content if it's different to avoid cursor jumping
                // But be more careful about when we update the content to preserve user changes
                const currentContent = contentRef.current.innerHTML;
                // Transform resource strings to visual spans for editing
                // Merge template and site resource strings for display, with template taking precedence
                const allResourceStrings = {
                    ...siteResourceStringsRef.current,
                    ...templateResourceStringsRef.current,
                };
                const transformedContent = transformResourceStringsToSpans(
                    tempContent || '<p>Start typing...</p>',
                    allResourceStrings
                );

                if (currentContent !== transformedContent && currentContent.trim() === '') {
                    // Only update if the editor is empty, otherwise preserve user changes
                    contentRef.current.innerHTML = transformedContent;
                }

                // Hide sidebar for inline editing
                hideSidebar();

                // Position toolbar relative to the component
                const { toolbar, cleanup: toolbarCleanup } = createEnhancedToolbar(
                    allFontsRef.current,
                    colorVariablesRef.current,
                    contentRef,
                    templateResourceStringsRef.current,
                    siteResourceStringsRef.current,
                    authorizationToken,
                    data
                );
                const rect = contentRef.current.getBoundingClientRect();
                toolbar.style.top = `${rect.top - 100}px`;
                toolbar.style.left = `${rect.left}px`;
                toolbar.style.width = `${Math.min(rect.width, 900)}px`;
                document.body.appendChild(toolbar);

                // Handle content changes (update local state only)
                const handleInput = (e: Event) => {
                    // Only update state for actual content changes, not selection changes
                    if (e.type === 'input') {
                        const newContent = contentRef.current?.innerHTML || '';
                        // console.log('Content changed:', newContent);
                        setTempContentRef.current(newContent);
                    }
                };

                // Don't interfere with keyboard events at all
                const handleKeyDown = (e: KeyboardEvent) => {
                    // Only handle specific keys that might need special behavior
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        stopEditing();
                    }
                };

                contentRef.current.addEventListener('input', handleInput);
                contentRef.current.addEventListener('keydown', handleKeyDown);

                // Track recent dropdown interactions to prevent premature closing
                let lastDropdownInteraction = 0;

                // Add listeners to track dropdown and AI dialog interactions
                toolbar.addEventListener('change', () => {
                    lastDropdownInteraction = Date.now();
                });

                toolbar.addEventListener('click', () => {
                    lastDropdownInteraction = Date.now();
                });

                // Track AI dialog interactions
                const aiDialogClickHandler = (e: Event) => {
                    const target = e.target as Element;
                    if (
                        target.closest('.rich-text-ai-dialog-container') ||
                        target.closest('.rich-text-ai-dialog')
                    ) {
                        lastDropdownInteraction = Date.now();
                    }
                };
                document.addEventListener('click', aiDialogClickHandler);

                // Improved click outside handler
                const improvedHandleClickOutside = (event: MouseEvent) => {
                    const target = event.target as Element;
                    const timeSinceDropdown = Date.now() - lastDropdownInteraction;

                    // Don't close if:
                    // 1. Clicking on content, toolbar, or dropdowns
                    // 2. Recent dropdown interaction (within 100ms)
                    // 3. Clicking on browser native dropdown elements
                    // 4. Clicking on AI dialog elements (both container and dialog itself)
                    if (
                        contentRef.current &&
                        !contentRef.current.contains(target) &&
                        !toolbar.contains(target) &&
                        !target.closest('.rich-text-color-dropdown') &&
                        !target.closest('.rich-text-ai-dialog-container') &&
                        !target.closest('.rich-text-ai-dialog') &&
                        !target.closest('select') &&
                        !target.closest('option') &&
                        target.tagName !== 'OPTION' &&
                        timeSinceDropdown > 100
                    ) {
                        stopEditing();
                    }
                };

                // Delay adding the click listener to prevent immediate closing
                setTimeout(() => {
                    document.addEventListener('mousedown', improvedHandleClickOutside);
                }, 200);

                const stopEditing = () => {
                    if (contentRef.current) {
                        contentRef.current.contentEditable = 'false';
                        const finalContent = contentRef.current.innerHTML;
                        saveContent(finalContent, widgetInstanceId);
                    }

                    const existingToolbar = document.querySelector('.rich-text-editable-toolbar');
                    if (existingToolbar) {
                        existingToolbar.remove();
                    }

                    // Restore sidebar
                    showSidebar();

                    setIsEditingRef.current(false);
                };

                return () => {
                    contentRef.current?.removeEventListener('input', handleInput);
                    contentRef.current?.removeEventListener('keydown', handleKeyDown);
                    document.removeEventListener('mousedown', improvedHandleClickOutside);
                    document.removeEventListener('click', aiDialogClickHandler);
                    toolbarCleanup(); // Clean up toolbar event listeners
                    toolbar.remove();
                    // Restore sidebar when done
                    showSidebar();
                };
            }
        };

        if (isEditing && contentRef.current) {
            const cleanup = setupContentEditable();
            cleanupRef.current = cleanup;
        }
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
            }
        };
    }, [isEditing, widgetInstanceId]); // Removed unstable dependencies that cause unnecessary re-runs
};
